import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "./urqlHooks";

const GET_SIGNED_DOWNLOAD_URL = `query GET_SIGNED_DOWNLOAD_URL($url: String!, $companyId: String!) {
  getSignedDownloadUrl(url: $url, companyId: $companyId)
}`;

export const GetSignedDownloadUrl = () => {
  const [result, executeQuery] = useQuery({ 
    query: GET_SIGNED_DOWNLOAD_URL, 
    pause: true 
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery };
};

/**
 * Query to get a fresh pre-signed download URL for a document file.
 * Constructs the S3 key dynamically from document metadata instead of using stored URLs.
 */
export const GET_DOCUMENT_FILE_URL = `query GET_DOCUMENT_FILE_URL($documentId: String!, $fileType: String!, $companyId: String!) {
  getDocumentFileUrl(documentId: $documentId, fileType: $fileType, companyId: $companyId)
}`;

const DOCUMENTS = `
query GET_PAGINATED_DOCUMENTS($input: PaginatedInput!) {
    paginatedDocumentsByCompany(input: $input) {
      totalDocs
      docs {
        id
        currencyType
        companyId
        name
        description
        amount
        documentDate
        isHidden
        isManual
        documentType
        agendaId
        isReported
      labels
        macroCategory
        category
        eerrArea
        cashflowArea {
          id
          name
        }
        company {
          id
          name
        }
        meta {
          ... on TaxDocumentMeta {
            documentId
            documentCode
            originalAmount
            netAmount
            taxAmount
            state
            otherTaxes {
                name
                value
                taxRate
                code
              }
            issuedAt
            dueDate
            isPaid
            isReceived
            paymentDate
            issuerId
            issuerName
            receiverId
            receiverName
            taxRate
            remaining
            downloadURL
            download {
              pdf {
                url
              }
              xml {
                url
              }
              html {
                url  
              }
            }
            description {
              item
              name
              description
              quantity
              unit
              unit_price
              total_price
              discount
              discount_percentage
            }
            summary
            isValid
            isProtested
            inform {
             amountInform
             isInform
             remaining
             informDate
            }
            matchs {
              inMatch {
                id
                amount
                selected
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                }
                accountPlan {
                  level1 {
                    value
                    label
                  }
                  level2 {
                    value
                    label
                  }
                  level3 {
                    value
                    label
                  }
                }
              }
              outMatch {
                id
                amount
                selected
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                }
                accountPlan {
                  level1 {
                    value
                    label
                  }
                  level2 {
                    value
                    label
                  }
                  level3 {
                    value
                    label
                  }
                }
              }
            }
            innerComments {
              transactionId
              user {
                id
                name
                email
              }
              date
              comment
            }
          }
        }
        meta {
        ... on ObligationMeta {
          family
          familyLabel
          type
          typeLabel
          description
          detail {
            name
            description
            item
            quantity
            unit_price
            total_price
          }
          date
          number
          documentId
          amount
          rate
          referenceNumber
          currency
          isPaid
          remaining
          originalAmount
          documentDate
          taxRate
          issuerId
          issuerName
          receiverId
          receiverName
          isReceived
          matchs {
            inMatch {
              id
              amount
              selected
              matchSource
              origin
              executionId
              ruleId
              userRelated {
                userId
                firstName
                lastName
                date
                email
              }
              accountPlan {
                level1 {
                  value
                  label
                }
                level2 {
                  value
                  label
                }
                level3 {
                  value
                  label
                }
              }
            }
            outMatch {
              id
              amount
              selected
              matchSource
              origin
              executionId
              ruleId
              userRelated {
                userId
                firstName
                lastName
                date
                email
              }
              accountPlan {
                level1 {
                  value
                  label
                }
                level2 {
                  value
                  label
                }
                level3 {
                  value
                  label
                }
              }
            }
          }
        }
      }
      }
    }
  } 
`;

export const GetDocuments = ({
  companyId,
  pagination,
  filter,
  search,
  detalle,
  rut,
  newFilter,
  documentTypes,
  isMatch,
}) => {
  const [result, reexecuteQuery] = useQuery({
    query: DOCUMENTS,
    variables: {
      input: {
        companyId,
        pagination,
        filter,
        search,
        newFilter,
        documentTypes,
        isMatch,
      },
    },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

export const SETTLE_EXCEL = `
  mutation SETTLE_EXCEL(
    $data: typeMasive!
    $companyId: String!
  ) {
    settleExcel(
      data: $data
      companyId: $companyId
    )
  }
`;

export const SettleExcel = () => {
  const [result, reexecuteMutation] = useMutation(SETTLE_EXCEL);
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteMutation };
};

const DOWNLOAD_DOCUMENTS = ` mutation DOWNLOAD_DOCUMENTS($input: FilteredDocumentInput!) {
    downloadDocuments(input: $input)
}`;

export const DownloadDocuments = () => {
  const [result, executeMutation] = useMutation(DOWNLOAD_DOCUMENTS);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DOWNLOAD_FILTERED_MULTI_PAYOFF = ` mutation DOWNLOAD_FILTERED_MULTI_PAYOFF($input: FilteredDocumentInput!) {
    downloadFilteredMultiPayoff(input: $input)
}`;

export const DownloadFilteredMultiPayoff = () => {
  const [result, executeMutation] = useMutation(DOWNLOAD_FILTERED_MULTI_PAYOFF);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const MATCH_DTES = `mutation MATCH_DTES($match: MatchInput!) {
  matchTransactionDtes(match: $match) {
    id
    amount
  }
}`;

export const MatchDtes = () => {
  const [result, matchDtes] = useMutation(MATCH_DTES);
  const { data, fetching, error } = result;
  return { data, fetching, error, matchDtes };
};

const CLASSIFY_DTES = `mutation CLASSIFY_DTES($documents:[String!]!, $fieldKey: String!, $companyId: String!) {
  setCategory(documents:$documents, fieldKey: $fieldKey, companyId: $companyId)
}`;

export const ClassifyDtes = () => {
  const [result, classifyDtes] = useMutation(CLASSIFY_DTES);
  const { data, fetching, error } = result;
  return { data, fetching, error, classifyDtes };
};

const SETTLE_DOCUMENT_AND_POINT = `mutation SETTLE_DOCUMENT_AND_POINT(
  $id: String!,
  $companyId: String!,
  $originalAmount: Float!,
  $remaining: Float!,
  $isPaid: Boolean!,
  ) {
  settleBothDocumentAndPoint(
    id: $id,
    companyId: $companyId,
    originalAmount: $originalAmount,
    remaining: $remaining,
    isPaid: $isPaid,
  ) {
    id
    amount
  }
}`;

export const SettleBothDocumentAndPoint = () => {
  const [result, executeMutation] = useMutation(SETTLE_DOCUMENT_AND_POINT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const MATCH_DOCUMENT_WITH_COUNTER_PART = `mutation MATCH_DOCUMENT_WITH_COUNTER_PART($match: MatchInput!) {
  matchDocumentWithCounterPart(match: $match) {
      id
      companyId
      amount
      meta {
        ... on TaxDocumentMeta {
          documentId
          documentCode
          originalAmount
          netAmount
          taxAmount
          issuedAt
          dueDate
          downloadURL
          download {
            pdf {
              url
            }
            xml {
              url
            }
            html {
              url  
            }
          }
          isPaid
          isReceived
          paymentDate
          issuerId
          issuerName
          receiverId
          receiverName
          taxRate
          remaining
          summary
          matchs {
            inMatch {
              id
              amount
              selected
            }
            outMatch {
              id
              amount
              selected
            }
          }
        }
      }
      meta {
      ... on ObligationMeta {
        family
        familyLabel
        type
        typeLabel
        description
        date
        number
        amount
        currency
        isPaid
        remaining
        originalAmount
        taxRate
        issuerId
        issuerName
        receiverId
        receiverName
        isReceived
        exchange {
          originalAmount
          originalCurrency
          localAmount
          localCurrency
          exchangeRate
          exchangeDate
        }
        matchs {
          inMatch {
            id
            amount
            selected
          }
          outMatch {
            id
            amount
            selected
          }
        }
      }
    }
  }
}`;

export const MatchDocumentWithCounterPart = () => {
  const [result, executeMutation] = useMutation(
    MATCH_DOCUMENT_WITH_COUNTER_PART
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DELETE_MATCH_BETWEEN_DOCUMENT_WITH_COUNTER_PART = `mutation DELETE_MATCH_BETWEEN_DOCUMENT_WITH_COUNTER_PART(
  $transactionId: String!,
  $documentId: String!,
  $companyId: String!,
  ) {
  deleteMatchBetweenDocumentAndDocument(transactionId: $transactionId, documentId: $documentId, companyId: $companyId) {
      id
      companyId
      amount
      meta {
        ... on TaxDocumentMeta {
          documentId
          documentCode
          originalAmount
          netAmount
          taxAmount
          issuedAt
          downloadURL
          download {
            pdf {
              url
            }
            xml {
              url
            }
            html {
              url  
            }
          }
          dueDate
          isPaid
          isReceived
          paymentDate
          issuerId
          issuerName
          receiverId
          receiverName
          taxRate
          remaining
          summary
          matchs {
            inMatch {
              id
              amount
              selected
            }
            outMatch {
              id
              amount
              selected
            }
          }
        }
      }
      meta {
      ... on ObligationMeta {
        family
        familyLabel
        type
        typeLabel
        description
        date
        number
        amount
        currency
        isPaid
        remaining
        originalAmount
        taxRate
        issuerId
        issuerName
        receiverId
        receiverName
        isReceived
        exchange {
          originalAmount
          originalCurrency
          localAmount
          localCurrency
          exchangeRate
          exchangeDate
        }
        matchs {
          inMatch {
            id
            amount
            selected
          }
          outMatch {
            id
            amount
            selected
          }
        }
      }
    }
  }
}`;

export const DeleteMatchBetweenDocuments = () => {
  const [result, executeMutation] = useMutation(
    DELETE_MATCH_BETWEEN_DOCUMENT_WITH_COUNTER_PART
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const BOOK = `mutation GET_BOOK(
  $companyId: String!,
  $isReceived: Boolean!,
  $isBoleta: Boolean!,
  $period: String!,
  $documentType: String!
  ) {
    getRCVBook(
      companyId: $companyId,
      isReceived: $isReceived
      isBoleta: $isBoleta,
      period: $period,
      documentType: $documentType
      ) {
      data {
        _id
        documentDate
        amount
        documentCode
        documentId
        obligationId
        obligationType
        documentType
        summary
        receiverId
        receiverName
        issuerId
        issuerName
        taxableAmount
        nonHoldableTaxes
        nonRecoverableTaxes
        lateTaxes
        nonTaxableAmount
        taxAmount
        isReceived
      }
      resume {
        _id
        count
        summary
        netAmount
        taxAmount
        lateTaxes
        nonTaxableAmount
        taxableAmount
        amount
      }
    }
}`;

export const GetBookByPeriod = () => {
  const [result, executeMutation] = useMutation(BOOK);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DOWNLOAD_BOOK = `mutation DOWNLOAD_BOOK(
  $companyId: String!,
  $type: String!,
  $period: String!,
  $ids: [String!]!,
  ) {
    downloadRCVBook(
      companyId: $companyId,
      type: $type
      period: $period,
      ids: $ids,
    )
}`;

export const DownloadBook = () => {
  const [result, executeMutation] = useMutation(DOWNLOAD_BOOK);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DOWNLOAD_DIN_BOOK = `mutation DOWNLOAD_DIN_BOOK(
  $companyId: String!,
  $ids: [String!],
  $periods: [String!],
  $dateStart: String,
  $dateEnd: String,
  ) {
    downloadDinBook(
      companyId: $companyId,
      ids: $ids,
      periods: $periods,
      dateStart: $dateStart,
      dateEnd: $dateEnd,
    )
}`;

export const DownloadDinBook = () => {
  const [result, executeMutation] = useMutation(DOWNLOAD_DIN_BOOK);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_DOCS_DIN = `mutation GET_DOCS_DIN(
  $companyId: String!,
  $periods: [String!],
  $dateStart: String,
  $dateEnd: String
){
  getDocsDinBook(
    companyId: $companyId,
    periods: $periods,
    dateStart: $dateStart,
    dateEnd: $dateEnd
  ) {
    _id
    documentId
    paymentDate
    issuedAt
    netAmount
    taxAmount
    originalAmount
    taxRate
    form
    period
    rate
  }
}`;

export const GetDocumentsDin = () => {
  const [result, executeMutation] = useMutation(GET_DOCS_DIN);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_DOCS_DIN_BY_ID = `mutation GET_DOCS_DIN_BY_ID(
  $companyId: String!,
  $selectedDocs: [String!]!,
){
  getDocsDinBookById(
    companyId: $companyId,
    ids: $selectedDocs,
  ){
    _id
    documentId
    paymentDate
    issuedAt
    netAmount
    taxAmount
    originalAmount
    taxRate
    form
    period
    rate
  }
}`;

export const GetDocumentsDinById = () => {
  const [result, executeMutation] = useMutation(GET_DOCS_DIN_BY_ID);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const COUNT_DOCS_RCV = `mutation RCV_COUNT_DOCS(
  $companyId: String!,
  $type: String!,
  $periods: [String!],
  $dateStart: String,
  $dateEnd: String
) {
  countDocumentsRCVBook(
    companyId: $companyId,
    type: $type,
    periods: $periods,
    dateStart: $dateStart,
    dateEnd: $dateEnd
  )
}`;

export const CountDocumentsRCV = () => {
  const [result, executeMutation] = useMutation(COUNT_DOCS_RCV);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_DOCS_RCV = `mutation GET_DOCS_RCV(
  $companyId: String!,
  $type: String!,
  $periods: [String!],
  $dateStart: String,
  $dateEnd: String
){
  getDocsRCVBook(
    companyId: $companyId,
    type: $type,
    periods: $periods,
    dateStart: $dateStart,
    dateEnd: $dateEnd
  ) {
    data {
      _id
      documentDate
      amount
      documentCode
      documentId
      obligationId
      obligationType
      documentType
      summary
      receiverId
      receiverName
      issuerId
      issuerName
      taxableAmount
      nonHoldableTaxes
      nonRecoverableTaxes
      lateTaxes
      nonTaxableAmount
      taxAmount
      isReceived
      isValid
      isProtested
    }
    resume {
      _id
      count
      summary
      netAmount
      taxAmount
      grossAmount
      lateTaxes
      nonTaxableAmount
      taxableAmount
      amount
    }
  }
}`;

export const GetDocumentsRCV = () => {
  const [result, executeMutation] = useMutation(GET_DOCS_RCV);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};


const GET_DOCS_RCV_ID = `mutation GET_DOCS_RCV_ID(
  $selectedDocs: [String!],
  $companyId: String!,
){
  getDocsRCVBookById(
    ids: $selectedDocs,
    companyId: $companyId,
  ){
    data {
      _id
      documentDate
      amount
      documentCode
      documentId
      obligationId
      obligationType
      documentType
      summary
      receiverId
      receiverName
      issuerId
      issuerName
      taxableAmount
      nonHoldableTaxes
      nonRecoverableTaxes
      lateTaxes
      nonTaxableAmount
      taxAmount
      isReceived
      isValid
      isProtested

    }
    resume {
      _id
      count
      summary
      netAmount
      taxAmount
      grossAmount
      lateTaxes
      nonTaxableAmount
      taxableAmount
      amount
    }
  }
}`;


export const GetDocumentsRCVByID = () => {
  const [result, executeMutation] = useMutation(GET_DOCS_RCV_ID);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DOWNLOAD_MULTI_PAYOFF = `mutation DOWNLOAD_RCV_MULTI_PAYOFF(
  $companyId: String!,
  $type: String!,
  $periods: [String!],
  $dateStart: String,
  $dateEnd: String
){
  downloadMultiPayoff(
    companyId: $companyId,
    type: $type,
    periods: $periods,
    dateStart: $dateStart,
    dateEnd: $dateEnd
  )
}`;

export const DownloadMultiPayoff = () => {
  const [result, executeMutation] = useMutation(DOWNLOAD_MULTI_PAYOFF);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DOWNLOAD_MULTI_PAYOFF_BY_ID = `mutation DOWNLOAD_RCV_MULTI_PAYOFF_BY_ID(
  $type : String!,
  $selectedDocs: [String!],
  $companyId: String!,
){
  downloadMultiPayoffById(
    type: $type,
    ids: $selectedDocs,
    companyId: $companyId,
  )
}`;

export const DownloadMultiPayoffById = () => {
  const [result, executeMutation] = useMutation(DOWNLOAD_MULTI_PAYOFF_BY_ID);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const RCV_PERIODS = `mutation RCV_PERIODS(
  $companyId: String!,
  $type: String!,
  ) {
    getPeriodsAvailable(
      companyId: $companyId,
      type: $type
    )
}`;

export const RCVPeriods = () => {
  const [result, executeMutation] = useMutation(RCV_PERIODS);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const FIND_AND_SETTLE = `mutation FIND_AND_SETTLE(
  $input: FilteredDocumentInput!,
  ) {
    FindAndSettleDocumentMassive(
      input: $input,
    )
}`;

export const FindAndSettleDocuments = () => {
  const [result, executeMutation] = useMutation(FIND_AND_SETTLE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const FIND_AND_SETTLE_BY_ID = `mutation FIND_AND_SETTLE_BY_ID(
  $ids: [String!]!,
  $companyId: String!,
  ) {
    FindAndSettleDocumentById(
      ids: $ids,
      companyId: $companyId,
    )
}`;

export const FindAndSettleDocumentsById = () => {
  const [result, executeMutation] = useMutation(FIND_AND_SETTLE_BY_ID);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const FIND_AND_PARTIAL_SETTLE_BY_ID = `mutation FIND_AND_PARTIAL_SETTLE_BY_ID(
  $ids: [PartialSettle!]!,
  $companyId: String!,
  ) {
    FindAndSettlePartialsDocumentById(
      ids: $ids,
      companyId: $companyId,
    )
}`;

export const FindAndPartialSettleDocumentsById = () => {
  const [result, executeMutation] = useMutation(FIND_AND_PARTIAL_SETTLE_BY_ID);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const FIND_DOCUMENT_BY_ID = `query FIND_DOCUMENT_BY_ID($ids: [String!]!, $companyId: String!) {
  findDocumentsById(ids: $ids, companyId: $companyId) {
      id
      currencyType
      companyId
      name
      description
      amount
      documentDate
      isHidden
      isManual
      documentType
      labels
      agendaId
      isReported
      fieldKey
      accountKey
      costCenters {
        id
        label
        amount
        currency
      }
      costCentersCompleted
      costCentersModifiedByHand
      localAmount
      originalDueDate
      dueDate
      cashflowAreaId
      paymentDate
      entries {
        completed
        amount
        entryList {
          entryId
          entryType
          entryNumber
          entryDate
          period
        }
      }
      attachedFiles {
        fileName
        originalFileName
        uploadedAt
        uploadedBy
      }
      meta {
        ... on TaxDocumentMeta {
          documentId
          documentCode
          originalAmount
          netAmount
          nonTaxableAmount
          taxableAmount
          exchange {
            localAmount
            originalCurrency
            localCurrency
          }
          barCode
          downloadURL
          retentionHolder
          isValid
          isProtested
          state
          description {
                    item
                    name
                    description
                    quantity
                    unit
                    unit_price
                    total_price
                    discount
                    discount_percentage
                    url
                }
          matchs {
                  inMatch {
                      id
                      settleId
                      currencyType
                      amount
                      originalAmount
                      selected
                      type
                      origin
                      matchSource
                      currency
                      matchedAmount
                      matchCurrency
                      obligationType
                      userRelated {
                          userId
                          firstName
                          lastName
                          date
                          email
                      }
                      entries {
                        completed
                        amount
                        entryList {
                          entryId
                          entryType
                          entryNumber
                          entryDate
                          period
                        }
                      }
                  }
                  outMatch {
                      id
                      amount
                      selected
                      type
                      matchSource
                      obligationType
                      userRelated {
                          userId
                          firstName
                          lastName
                          date
                          email
                      }
                  }
              }         
          download {
            pdf {
              url
            }
            xml {
              url
            }
            html {
              url  
            }
            factoring {
              url  
            }
            billing {
              url  
            }
          }
          taxAmount
          issuedAt
          dueDate
          isPaid
          isUncollectible
          isReceived
          paymentDate
          issuerId
          issuerName
          receiverId
          receiverName
          taxRate
          factoring {
            cedido
            issuerId
            issuerName
            cesionDate
            cesiones {
              date
              cedenteId
              cedenteName
              cesionarioId
              cesionarioName
              amount
              status
            }
          }
          otherTaxes {
                name
                value
                taxRate
                code
              }
          commonTaxes
          nominaStatus {
                totalNomina
                totalSelected
                totalApproved
                inNominaProcess {
                    id
                    userSelected
                    amount
                    status
                    dateSelected
                    nameSelector
                    emailSelector
                    userToCreated
                    nominaId
                    dateToCreated
                    nominaName
                    userInformer
                    dateInformed
                    userToBank
                    dateToBank
                    userApproveReject
                    dateApproveReject
                    isMatched
                }
            }
          remaining
          summary
          type
          typeLabel
          period
          flexiblePeriod
          refDocuments {
            documentId
            documentCode
            name
            rut
            amount
            id
          }
          innerComments {
            user {
              id
              name
              email
            }
            date
            comment
          }
        }
        ... on ObligationMeta {
        family
        familyLabel
        type
        typeLabel
        creationFor
        descriptionText: description
        date
        number
        localAmount
        amount
        currency
        isPaid
        remaining
        originalAmount
        taxRate
        rate
        issuerId
        issuerName
        receiverId
        receiverName
        isReceived
        creditDocumentId
        parentCreditId
        period
        flexiblePeriod
        login
        transactionNumber
        ticket
        empresa {
          name
        }
        exchange {
          originalAmount
          originalCurrency
          localAmount
          localCurrency
          exchangeRate
          exchangeDate
        }
        matchs {
                    inMatch {
                        id
                        settleId
                        currencyType
                        amount
                        originalAmount
                        selected
                        type
                        origin
                        matchSource
                        currency
                        matchedAmount
                        matchCurrency
                        obligationType
                        userRelated {
                            userId
                            firstName
                            lastName
                            date
                            email
                        }
                        entries {
                          completed
                          amount
                          entryList {
                            entryId
                            entryType
                            entryNumber
                            entryDate
                            period
                          }
                        }
                    }
                    outMatch {
                        id
                        amount
                        selected
                        type
                        matchSource
                        obligationType
                        userRelated {
                            userId
                            firstName
                            lastName
                            date
                            email
                        }
                        entries {
                          completed
                          amount
                          entryList {
                            entryId
                            entryType
                            entryNumber
                            entryDate
                            period
                          }
                        }
                    }
                }
        detail {
          name
          description
          item
          quantity
          unit_price
          total_price
        }
        nominaStatus {
          totalNomina
          totalSelected
          totalApproved
          inNominaProcess {
            id
            userSelected
            amount
            status
            dateSelected
            nameSelector
            emailSelector
            userToCreated
            nominaId
            dateToCreated
            nominaName
            userInformer
            dateInformed
            userToBank
            dateToBank
            userApproveReject
            dateApproveReject
            isMatched
          }
        }
        innerComments {
          user {
            id
            name
            email
          }
          date
          comment
        }
        isAISuggestion
        suggestedForTransactionId
        aiPatternId
        aiConfidenceScore
        }
        ... on CreditsMeta {
        installments
        interest
        stock
        interestCLP
        stockCLP
        installmentCLP
        totalPayments
        paymentNumber
        isPaid
        identificator
        creditType
        period
        flexiblePeriod
        remaining
        originalAmount
        matchs {
                    inMatch {
                        id
                        settleId
                        currencyType
                        amount
                        originalAmount
                        selected
                        type
                        origin
                        matchSource
                        currency
                        matchedAmount
                        matchCurrency
                        obligationType
                        userRelated {
                            userId
                            firstName
                            lastName
                            date
                            email
                        }
                        entries {
                          completed
                          amount
                          entryList {
                            entryId
                            entryType
                            entryNumber
                            entryDate
                            period
                          }
                        }
                    }
                    outMatch {
                        id
                        amount
                        selected
                        type
                        matchSource
                        obligationType
                        userRelated {
                            userId
                            firstName
                            lastName
                            date
                            email
                        }
                        entries {
                          completed
                          amount
                          entryList {
                            entryId
                            entryType
                            entryNumber
                            entryDate
                            period
                          }
                        }
                    }
                }
        innerComments {
          user {
            id
            name
            email
          }
          date
          comment
        }
        }
      }
  }
}`;

// Query separada y liviana para candidatos de rendición↔factura.
// Solo se usa desde reconcileDocumentSideBar cuando se abre el sidebar.
const FIND_DOCUMENT_CANDIDATES = `query FIND_DOCUMENT_CANDIDATES($ids: [String!]!, $companyId: String!) {
  findDocumentsById(ids: $ids, companyId: $companyId) {
    id
    _id
    aiInvoiceMovementMatchSuggestion {
      invoiceId
      status
      confidenceScore
      invoiceAmount
      totalMatchedAmount
      absoluteError
      relativeError
      selectedMovementIds
      selectedMovements {
        id
        _id
        amount
        remaining
        date
        description
        issuer
        issuerName
        type
        isReceived
      }
    }
    strongMatchCandidates {
      documentId
      document {
        id
        _id
        documentType
        companyId
        amount
        currencyType
        documentDate
        isHidden
        meta {
          ... on TaxDocumentMeta {
            documentId
            documentCode
            isReceived
            isPaid
            remaining
            originalAmount
            issuerId
            issuerName
            receiverId
            receiverName
            summary
          }
          ... on ObligationMeta {
            type
            typeLabel
            family
            familyLabel
            isReceived
            isPaid
            remaining
            amount
            currency
            issuerId
            issuerName
            receiverId
            receiverName
            creditDocumentId
            parentCreditId
            description
          }
        }
      }
    }
    weakMatchCandidates {
      documentId
      document {
        id
        _id
        documentType
        companyId
        amount
        currencyType
        documentDate
        isHidden
        meta {
          ... on TaxDocumentMeta {
            documentId
            documentCode
            isReceived
            isPaid
            remaining
            originalAmount
            issuerId
            issuerName
            receiverId
            receiverName
            summary
          }
          ... on ObligationMeta {
            type
            typeLabel
            family
            familyLabel
            isReceived
            isPaid
            remaining
            amount
            currency
            issuerId
            issuerName
            receiverId
            receiverName
            description
          }
        }
      }
    }
  }
}`;

export const FindDocumentById = (variables) => {
  // Stabilize ids + reexecuteQuery:
  // Many callers include `reexecuteQuery` in effect deps. If we return a new function each render,
  // they can accidentally refetch forever.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idsKey = useMemo(() => {
    const ids = variables?.ids || [];
    return Array.isArray(ids) ? ids.filter(Boolean).join(",") : "";
  }, [variables?.ids?.join?.(",")]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableIds = useMemo(() => {
    const ids = variables?.ids || [];
    return Array.isArray(ids) ? [...ids] : [];
  }, [idsKey]);

  const shouldPause =
    !stableIds ||
    stableIds.length === 0 ||
    stableIds.includes(null) ||
    stableIds.includes(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableVariables = useMemo(() => {
    return { ...(variables || {}), ids: stableIds };
  }, [idsKey]);
  
  const [result, reexecuteQuery] = useQuery({
    query: FIND_DOCUMENT_BY_ID,
    variables: stableVariables,
    pause: shouldPause, // Skip query when no valid IDs are provided
    requestPolicy: 'network-only', // Always fetch fresh data to reflect deleted documents
  });

  // Wrap reexecuteQuery to prevent calls with empty IDs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const safeReexecuteQuery = useCallback((options) => {
    const currentIds = options?.variables?.ids || stableIds;
    if (!currentIds || currentIds.length === 0) {
      // Don't execute if IDs are empty
      return Promise.resolve({ data: null, error: null });
    }
    return reexecuteQuery(options);
  }, [reexecuteQuery, idsKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => {
    const { data, fetching, error } = result;
    return { data, fetching, error, reexecuteQuery: safeReexecuteQuery };
  }, [result.data, result.fetching, result.error, safeReexecuteQuery]);
};

/**
 * Hook para obtener candidatos de rendición↔factura de un documento.
 * Separado de FindDocumentById para no penalizar la carga principal del documento.
 */
export const FindDocumentCandidates = (variables) => {
  const idsKey = useMemo(() => {
    const ids = variables?.ids || [];
    return Array.isArray(ids) ? ids.filter(Boolean).join(",") : "";
  }, [variables?.ids?.join?.(",")]);

  const stableIds = useMemo(() => {
    const ids = variables?.ids || [];
    return Array.isArray(ids) ? [...ids] : [];
  }, [idsKey]);

  const shouldPause =
    !stableIds || stableIds.length === 0 ||
    stableIds.includes(null) || stableIds.includes(undefined);

  const stableVariables = useMemo(() => {
    return { ids: stableIds };
  }, [idsKey]);

  const [result, reexecuteQuery] = useQuery({
    query: FIND_DOCUMENT_CANDIDATES,
    variables: stableVariables,
    pause: shouldPause,
    requestPolicy: 'network-only',
  });

  return useMemo(() => {
    const { data, fetching, error } = result;
    return { data, fetching, error, reexecuteQuery };
  }, [result.data, result.fetching, result.error, reexecuteQuery]);
};

const GET_AUDITOR_DATA = `
query GET_AUDITOR_DATA($companyId: String!, $period: String!) {
  getAuditorData(companyId: $companyId, period: $period) {
    compra {
      rows {
        documentCode
        documentType
        summary
        isReceived
        source
        count
        netAmount
        taxableAmount
        nonTaxableAmount
        recoverableTaxes
        nonRecoverableTaxes
        commonTaxes
        taxAmount
        amount
        documents
        missingEntryCount
        amountExceedsCount
        excludedAppearsCount
        wrongPeriodCount
      }
      totals {
        documentCode
        documentType
        summary
        isReceived
        source
        count
        netAmount
        taxableAmount
        nonTaxableAmount
        recoverableTaxes
        nonRecoverableTaxes
        commonTaxes
        taxAmount
        amount
      }
    }
    venta {
      rows {
        documentCode
        documentType
        summary
        isReceived
        source
        count
        netAmount
        taxableAmount
        nonTaxableAmount
        recoverableTaxes
        nonRecoverableTaxes
        commonTaxes
        taxAmount
        amount
        documents
        missingEntryCount
        amountExceedsCount
        excludedAppearsCount
        wrongPeriodCount
      }
      totals {
        documentCode
        documentType
        summary
        isReceived
        source
        count
        netAmount
        taxableAmount
        nonTaxableAmount
        recoverableTaxes
        nonRecoverableTaxes
        commonTaxes
        taxAmount
        amount
      }
    }
    honorarios {
      rows {
        documentCode
        documentType
        summary
        isReceived
        source
        count
        netAmount
        taxableAmount
        nonTaxableAmount
        recoverableTaxes
        nonRecoverableTaxes
        commonTaxes
        amount
        documents
        missingEntryCount
        amountExceedsCount
        excludedAppearsCount
        wrongPeriodCount
      }
    }
    retenciones {
      rows {
        documentCode
        documentType
        summary
        isReceived
        source
        count
        amount
        taxAmount
        documents
      }
    }
    orphanEmissionEntries {
      entryId
      entryNumber
      entryDate
    }
    orphanEmissionEntryCount
  }
}
`;

export const GetAuditorData = ({ companyId, period }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_AUDITOR_DATA,
    variables: { companyId, period },
    pause: !companyId || !period,
    requestPolicy: 'network-only',
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const MOVE_AUDITOR_RECOVERABLE_TAXES_TO_NON_RECOVERABLE = `
mutation MOVE_AUDITOR_RECOVERABLE_TAXES_TO_NON_RECOVERABLE(
  $companyId: String!
  $period: String!
  $documentCode: String!
) {
  moveAuditorRecoverableTaxesToNonRecoverable(
    companyId: $companyId
    period: $period
    documentCode: $documentCode
  ) {
    nModified
    amountMoved
    skippedCount
  }
}
`;

export const MoveAuditorRecoverableTaxesToNonRecoverable = () => {
  const [result, executeMutation] = useMutation(
    MOVE_AUDITOR_RECOVERABLE_TAXES_TO_NON_RECOVERABLE
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const MOVE_AUDITOR_NON_RECOVERABLE_TAXES_TO_RECOVERABLE = `
mutation MOVE_AUDITOR_NON_RECOVERABLE_TAXES_TO_RECOVERABLE(
  $companyId: String!
  $period: String!
  $documentCode: String!
) {
  moveAuditorNonRecoverableTaxesToRecoverable(
    companyId: $companyId
    period: $period
    documentCode: $documentCode
  ) {
    nModified
    amountMoved
    skippedCount
  }
}
`;

export const MoveAuditorNonRecoverableTaxesToRecoverable = () => {
  const [result, executeMutation] = useMutation(
    MOVE_AUDITOR_NON_RECOVERABLE_TAXES_TO_RECOVERABLE
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_AUDITOR_BOOK_DETAIL = `
query GET_AUDITOR_BOOK_DETAIL(
  $companyId: String!
  $period: String!
  $documentCode: String!
  $isReceived: Boolean!
  $limit: Int
  $skip: Int
  $detailMatch: AuditorBookDetailMatch
) {
  getAuditorBookDetail(
    companyId: $companyId
    period: $period
    documentCode: $documentCode
    isReceived: $isReceived
    limit: $limit
    skip: $skip
    detailMatch: $detailMatch
  ) {
    totalCount
    period
    documentCode
    isReceived
    rows {
      documentId
      documentFolio
      summary
      documentDate
      netAmount
      amount
      hasEmissionEntryInPeriod
      emissionEntryNumbers
      emissionEntryIds
      emissionEntryTypes
      emissionEntryDates
      emissionEntryTotalAmount
      counterpartName
      counterpartRut
      missingEntry
      amountExceeds
      isNoIncluir
      isProtested
      wrongPeriod
      documentPeriod
    }
  }
}
`;

export const GetAuditorBookDetail = ({
  companyId,
  period,
  documentCode,
  isReceived,
  limit,
  skip,
  detailMatch,
  pause,
}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_AUDITOR_BOOK_DETAIL,
    variables: {
      companyId,
      period,
      documentCode,
      isReceived,
      limit,
      skip,
      detailMatch,
    },
    pause:
      pause ||
      !companyId ||
      !period ||
      documentCode == null ||
      documentCode === '' ||
      typeof isReceived !== 'boolean',
    requestPolicy: 'network-only',
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const UPDATE_XML_BY_MONTH = `mutation UPDATE_XML_BY_MONTH(
  $companyId: String!,
  $month: Float!,
  $year: Float!,
  ) {
    updateXMLByMonth(
      companyId: $companyId,
      month: $month,
      year: $year,
    )
}`;

export const UpdateXMLByMonth = () => {
  const [_result, executeMutation] = useMutation(UPDATE_XML_BY_MONTH);
  return executeMutation;
};

const PAY_UNPAY_BY_ID = `mutation PAY_UNPAY_BY_ID(
  $companyId: String!,
  $data: typeMasive!,
){
  payUnpayById(
    companyId: $companyId,
    data: $data
  )
}`;

export const PayUnppayById = () => {
  const [result, executeMutation] = useMutation(PAY_UNPAY_BY_ID);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_ISSUER_PENDING_DOCS = `query GET_ISSUER_PEDNING_DOCS($issuerId: String!, $companyId: String!) {
  getIssuerPendingDocs(issuerId: $issuerId, companyId: $companyId) {
      id
      currencyType
      companyId
      name
      description
      amount
      documentDate
      isHidden
      isManual
      documentType
      fieldKey
      meta {
        ... on TaxDocumentMeta {
          documentId
          documentCode
          originalAmount
          netAmount
          downloadURL
          download {
            pdf {
              url
            }
            xml {
              url
            }
            html {
              url  
            }
          }
          taxAmount
          issuedAt
          dueDate
          isPaid
          isReceived
          paymentDate
          issuerId
          issuerName
          receiverId
          receiverName
          taxRate
          remaining
          summary
          remaining
          period
          flexiblePeriod
        }
      }
  }
}`;

export const GetIssuerPendingDocs = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_ISSUER_PENDING_DOCS,
    variables,
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const ACCEPT_SII_DOCUMENTS = `mutation ACCEPT_SII_DOCUMENTS($companyId: String!,
  $codeEvent: String!, 
  $documents: [DocAcceptanceParams!]!) {
    acceptDocumentsSii(
      companyId: $companyId
      codeEvent: $codeEvent
      documents: $documents
    ) {
      status
      message
      data
      warning
    }
}`

export const AcceptSiiDoc = () => {
  const [result, acceptanceSiiDoc] = useMutation(ACCEPT_SII_DOCUMENTS);
  return { acceptanceSiiDoc, result }
};

const MODIFY_DOCUMENT = `mutation MODIFY_DOCUMENT(
    $companyId: String!,
    $input: [DocumentToModify!]!,
  ){
    modifyDocument(
      companyId: $companyId,
      input: $input,
    ) {
      ok
      nModified
    }
  }`;

export const ModifyDocument = () => {
  const [result, executeMutation] = useMutation(MODIFY_DOCUMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const SETTLE_DOCUMENT = `mutation SETTLE_DOCUMENT(
    $companyId: String!,
    $input: [DocumentToSettle!]!,
  ){
    settleDocument(
      companyId: $companyId,
      input: $input,
    ) {
      ok
      nModified
    }
  }`;

export const SettleDocument = () => {
  const [result, executeMutation] = useMutation(SETTLE_DOCUMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CREATE_CHECK_DOCUMENT = `mutation CREATE_CHECK_DOCUMENT(
    $companyId: String!,
    $documentId: String!,
    $input: [CheckDocumentInput!]!,
  ){
    createCheckDocument(
      companyId: $companyId,
      documentId: $documentId,
      input: $input,
    ) {
      id
    }
  }`;

export const CreateCheckDocument = () => {
  const [result, executeMutation] = useMutation(CREATE_CHECK_DOCUMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const SETTLE_CONFLICT_DOC_NOMINA = `mutation SETTLE_CONFLICT_DOC_NOMINA(
    $documentId: String!,
    $companyId: String!,
    $isRelated: Boolean!,
    $selectionIds: [String!] ) {
      settleConflictDocumentNomina(
        documentId: $documentId
        companyId: $companyId
        isRelated: $isRelated
        selectionIds: $selectionIds
      ) {
        status
        message
        data
      }
  }`

export const SettleConflictDocNomina = () => {
  const [_result, settleConflictSocNomina] = useMutation(SETTLE_CONFLICT_DOC_NOMINA)
  return settleConflictSocNomina;
}

const CREATE_DTE_INNER_COMMENT = `
mutation CREATE_DTE_INNER_COMMENT($input: InnerComment!, $companyId: String!) {
  createDteInnerComment(input: $input, companyId: $companyId) {
    id
  }
}
`

export const CreateDteInnerComment = () => {
  const [result, executeMutation] = useMutation(CREATE_DTE_INNER_COMMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_BUK_IMPUESTO_UNICO = `mutation getBukImpuestoUnicoAmount(
  $companyUrl: String!, $token:String!, $date:DateTime! 
){
  getBukImpuestoUnicoAmount(
    companyUrl: $companyUrl,
    token:$token,
    date:$date
  ){
    status
    message
    amount
  }
}`;

export const GetBukImpuestoUnicoAmount = () => {
  const [result, executeMutation] = useMutation(GET_BUK_IMPUESTO_UNICO);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const UPDATE_DOCUMENTs_CASH_FLOW_AREA_ID = `mutation updateDocumentsCashflowAreaId(
  $input: UpdateDocumentsCashflowAreaIdInput!
){
  updateDocumentsCashflowAreaId(
    input: $input
  ){
    ok
    nModified
  }
}`;

export const UpdateDocumentsCashflowAreaId = () => {
  const [result, executeMutation] = useMutation(UPDATE_DOCUMENTs_CASH_FLOW_AREA_ID);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const REMOVE_NOTE_FROM_DOCUMENT = `mutation removeNoteFromDocument(
  $companyId: String!,
  $documentId: String!,
  $noteIndexToRemove: Float!,
){
  removeNoteFromDocument(
    companyId: $companyId,
    documentId: $documentId,
    noteIndexToRemove: $noteIndexToRemove
  ){
    ok
    nModified
  }
}`;

export const RemoveNoteFromDocument = () => {
  const [result, executeMutation] = useMutation(REMOVE_NOTE_FROM_DOCUMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const UPDATE_NOTE_IN_DOCUMENT = `mutation updateNoteInDocument(
  $companyId: String!,
  $documentId: String!,
  $noteIndex: Float!,
  $comment: String!,
) {
  updateNoteInDocument(
    companyId: $companyId,
    documentId: $documentId,
    noteIndex: $noteIndex,
    comment: $comment
  ) {
    ok
    nModified
  }
}`;

export const UpdateNoteInDocument = () => {
  const [result, executeMutation] = useMutation(UPDATE_NOTE_IN_DOCUMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const SET_PINNED_DOCUMENT_NOTE = `mutation setPinnedDocumentNote(
  $companyId: String!,
  $documentId: String!,
  $noteIndex: Float!,
  $isPinned: Boolean!,
) {
  setPinnedDocumentNote(
    companyId: $companyId,
    documentId: $documentId,
    noteIndex: $noteIndex,
    isPinned: $isPinned
  ) {
    ok
    nModified
  }
}`;

export const SetPinnedDocumentNote = () => {
  const [result, executeMutation] = useMutation(SET_PINNED_DOCUMENT_NOTE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const ASSOCIATE_COST_CENTERS_TO_DOCUMENT = `
mutation associateCostCentersToDocument(
  $documentsIds: [String!]!,
  $companyId: String!,
  $costCenters: [DocumentCostCenterInput!]!
  $isMassive: Boolean!,
) {
  associateCostCentersToDocument(
    companyId: $companyId,
    documentsIds: $documentsIds,
    costCenters: $costCenters,
    isMassive: $isMassive,
  ) {
    ok
    nModified
  }
}`;

export const AssociateCostCentersToDocument = () => {
  const [result, executeMutation] = useMutation(
    ASSOCIATE_COST_CENTERS_TO_DOCUMENT
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const REMOVE_COST_CENTER_FROM_DOCUMENT = `
mutation removeCostCenterFromDocument(
  $companyId: String!,
  $documentId: String!,
  $costCenterId: String!
) {
  removeCostCenterFromDocument(
    companyId: $companyId,
    documentId: $documentId,
    costCenterId: $costCenterId
  ) {
    ok
    nModified
  }
}`;

export const RemoveCostCenterFromDocument = () => {
  const [result, executeMutation] = useMutation(
    REMOVE_COST_CENTER_FROM_DOCUMENT
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_COST_CENTERS_BY_DOCUMENTS = `
query getCostCentersByDocuments($documentIds: [String!]!, $companyId: String!) {
  getCostCentersByDocuments(documentIds: $documentIds, companyId: $companyId) {
    id
    label
    amount
    currency
  }
}`;

export const GetCostCentersByDocument = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_COST_CENTERS_BY_DOCUMENTS,
    variables,
    requestPolicy: 'cache-and-network',
    pause: !variables?.documentIds || variables?.documentIds?.length === 0 || variables?.documentIds?.includes(null) || variables?.documentIds?.includes(undefined), // Skip query when no valid documentIds are provided
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const DOCUMENT_BY_ID = `query Document(
  $id: String!
  $companyId: String!
){
Document(
  id: $id
  companyId: $companyId
){amount
  name
  description
  documentType
  isHidden
  companyId
  currencyType
  documentDate
  id
  isManual
  cashflowArea {
    id
    name
  }
  meta {
    ... on TaxDocumentMeta {
      documentId
      documentCode
      originalAmount
      netAmount
        taxAmount
        otherTaxes {
           name
           value
          }
      issuedAt
      isPaid
      paymentDate
      isReceived
      issuerId
      issuerName
      receiverId
      receiverName
      taxRate
      remaining
      summary
      innerComments {
        user {
          id
          name
          email
        }
        date
        comment
      }
      matchs {
        inMatch {
          id
          amount
          selected
          currencyType
          userRelated {
            userId
            firstName
            lastName
            date
          }
          accountPlan {
            level1 {
              value
              label
            }
            level2 {
              value
              label
            }
            level3 {
              value
              label
            }
          }
          entries {
            completed
            amount
            entryList {
              entryId
              entryType
              entryNumber
              entryDate
              period
            }
          }
        }
        outMatch {
          id
          amount
          selected
          userRelated {
            userId
            firstName
            lastName
            date
          }
          accountPlan {
            level1 {
              value
              label
            }
            level2 {
              value
              label
            }
            level3 {
              value
              label
            }
          }
          entries {
            completed
            amount
            entryList {
              entryId
              entryType
              entryNumber
              entryDate
              period
            }
          }
        }
      }
    }
  }
  meta {
    ... on ObligationMeta {
      family
      familyLabel
      type
      typeLabel
      description
      detail {
        item
        name
        description
        quantity
        unit
        unit_price
        total_price
        discount
        discount_percentage
        url
      }
      date
      number
      amount
      currency
      isPaid
      remaining
      originalAmount
      taxRate
      issuerId
      issuerName
      receiverId
      receiverName
      creditDocumentId
      parentCreditId
      exchange {
        originalAmount
        originalCurrency
        localAmount
        localCurrency
        exchangeRate
        exchangeDate
      }
      innerComments {
        user {
          id
          name
          email
        }
        date
        comment
      }
      matchs {
        inMatch {
          id
          amount
          selected
          currencyType
          userRelated {
            userId
            firstName
            lastName
            date
          }
          accountPlan {
            level1 {
              value
              label
            }
            level2 {
              value
              label
            }
            level3 {
              value
              label
            }
          }
          entries {
            completed
            amount
            entryList {
              entryId
              entryType
              entryNumber
              entryDate
              period
            }
          }
        }
        outMatch {
          id
          amount
          selected
          userRelated {
            userId
            firstName
            lastName
            date
          }
          accountPlan {
            level1 {
              value
              label
            }
            level2 {
              value
              label
            }
            level3 {
              value
              label
            }
          }
          entries {
            completed
            amount
            entryList {
              entryId
              entryType
              entryNumber
              entryDate
              period
            }
          }
        }
      }
    }
  }
  meta {
    ... on CreditsMeta {
      installments
      interest
      stock
      totalPayments
      paymentNumber
      isPaid
      identificator
    }
  }
}
}`

export const GetDocumentById = (id) => {
  const [result, reexecuteQuery] = useQuery({
    query: DOCUMENT_BY_ID,
    variables: { id },
    pause: !id || id === undefined || id === null, // Skip query when no valid ID is provided
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_ALL_DOCUMENTS_BY_CONTACT = `query GET_ALL_DOCUMENTS_BY_CONTACT($contactId: String! $companyId: String! $pagination:PaginationInput! $documentType: String! $obligationType: String!) {
  getAllDocumentsByContact(contactId: $contactId companyId: $companyId pagination: $pagination documentType:$documentType obligationType:$obligationType) {
    documents {
        _id
        currencyType
        companyId
        name
        description
        amount
        documentDate
        isHidden
        isManual
        documentType
        macroCategory
        category
        eerrArea
        dueDate
        cashflowArea {
          id
          name
        }
        company {
          id
          name
        }
        meta {
          ... on TaxDocumentMeta {
            documentId
            documentCode
            originalAmount
            netAmount
            taxAmount
            state
            otherTaxes {
                name
                value
                taxRate
                code
              }
            issuedAt
            dueDate
            isPaid
            isReceived
            paymentDate
            issuerId
            issuerName
            receiverId
            receiverName
            taxRate
            remaining
            downloadURL
            summary
            isValid
            isProtested
            inform {
             amountInform
             isInform
             remaining
             informDate
            }
            matchs {
              inMatch {
                id
                amount
                selected
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                }
                accountPlan {
                  level1 {
                    value
                    label
                  }
                  level2 {
                    value
                    label
                  }
                  level3 {
                    value
                    label
                  }
                }
              }
              outMatch {
                id
                amount
                selected
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                }
                accountPlan {
                  level1 {
                    value
                    label
                  }
                  level2 {
                    value
                    label
                  }
                  level3 {
                    value
                    label
                  }
                }
              }
            }
          }
        }
        meta {
        ... on ObligationMeta {
          family
          familyLabel
          type
          typeLabel
          detail {
            name
            description
            item
            quantity
            unit_price
            total_price
          }
          date
          number
          documentId
          amount
          rate
          referenceNumber
          currency
          isPaid
          remaining
          originalAmount
          documentDate
          taxRate
          issuerId
          issuerName
          receiverId
          receiverName
          isReceived
          matchs {
            inMatch {
              id
              amount
              selected
              matchSource
              origin
              executionId
              ruleId
              userRelated {
                userId
                firstName
                lastName
                date
                email
              }
              accountPlan {
                level1 {
                  value
                  label
                }
                level2 {
                  value
                  label
                }
                level3 {
                  value
                  label
                }
              }
            }
            outMatch {
              id
              amount
              selected
              matchSource
              origin
              executionId
              ruleId
              userRelated {
                userId
                firstName
                lastName
                date
                email
              }
              accountPlan {
                level1 {
                  value
                  label
                }
                level2 {
                  value
                  label
                }
                level3 {
                  value
                  label
                }
              }
            }
          }
        }
      }    
    }
    totalToPay
    totalOverdue
    totalDocuments
  }
}`;

export const GetAllDocumentsByContact = ({ contactId, pagination, documentType, obligationType }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_ALL_DOCUMENTS_BY_CONTACT,
    variables: { contactId, pagination, documentType, obligationType },
  });
  const { data, fetching, error } = result;

  return { data, fetching, error, reexecuteQuery };
};

const GENERATE_EXCEL_BY_CONTACT_DOCUMENTS = `mutation GENERATE_EXCEL_BY_CONTACT_DOCUMENTS(
  $contactId: String!
  $companyId: String!
){
  generateExcelByContactDocuments(contactId: $contactId, companyId: $companyId)
}`;

export const GenerateExcelByContactDocuments = () => {
  const [result, executeMutation] = useMutation(GENERATE_EXCEL_BY_CONTACT_DOCUMENTS);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CUADRATURA_DOCUMENTS = `
  query CUADRATURA_DOCUMENTS($companyId: String!, $period: String!, $filter: CuadraturaFilterInput) { 
    getCuadraturaDocuments(companyId: $companyId, period: $period, filter: $filter) {
      strongMatchCount
      label
      amount
      period
      documents {
        id
        amount
        remaining
        issuerName
        documentDate
        documentId
        summary
      }
      obligation {
          id
          amount
          documentType
          name
          description
          isHidden
          companyId
          currencyType
          documentDate
          meta {
            ... on ObligationMeta {
              family
              familyLabel
              type
              typeLabel
              description
              date
              number
              amount
              currency
              isPaid
              remaining
              originalAmount
              taxRate
              issuerId
              issuerName
              receiverId
              receiverName
              matchs {
                inMatch {
                  id
                  amount
                  selected
                  currencyType
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                  }
                  accountPlan {
                    level1 {
                      value
                      label
                    }
                    level2 {
                      value
                      label
                    }
                    level3 {
                      value
                      label
                    }
                  }
                }
                outMatch {
                  id
                  amount
                  selected
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                  }
                  accountPlan {
                    level1 {
                      value
                      label
                    }
                    level2 {
                      value
                      label
                    }
                    level3 {
                      value
                      label
                    }
                  }
                }
              }
            }
          }
      }
    }
  }
`;

export const GetCuadraturaDocuments = ({ companyId, period, filter }) => {
  const [result, reexecuteQuery] = useQuery({
    query: CUADRATURA_DOCUMENTS,
    variables: { companyId, period, filter },
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const LINKED_CUADRATURA_DOCUMENTS = `
  query LINKED_CUADRATURA_DOCUMENTS($companyId: String!, $period: String!) { 
    getLinkedCuadraturaDocuments(companyId: $companyId, period: $period) {
      strongMatchCount
      label
      amount
      period
      linkedDocuments {
          id
          amount
          documentType
          name
          isHidden
          companyId
          currencyType
          documentDate
          meta {
        ... on TaxDocumentMeta {
      documentId
      documentCode
      originalAmount
      netAmount
        taxAmount
        otherTaxes {
           name
           value
          }
      issuedAt
      isPaid
      paymentDate
      isReceived
      issuerId
      issuerName
      receiverId
      receiverName
      taxRate
      remaining
      summary
      nominaStatus {
        totalNomina
        totalSelected
        totalApproved
        inNominaProcess {
          nominaName
          emailSelector
        }
      }
      matchs {
        inMatch {
          id
          amount
          selected
          currencyType
          userRelated {
            userId
            firstName
            lastName
            date
          }
          accountPlan {
            level1 {
              value
              label
            }
            level2 {
              value
              label
            }
            level3 {
              value
              label
            }
          }
        }
        outMatch {
          id
          amount
          selected
          userRelated {
            userId
            firstName
            lastName
            date
          }
          accountPlan {
            level1 {
              value
              label
            }
            level2 {
              value
              label
            }
            level3 {
              value
              label
            }
          }
        }
      }}
    }
      }
      obligation {
          id
          amount
          documentType
          name
          description
          isHidden
          companyId
          currencyType
          documentDate
          meta {
            ... on ObligationMeta {
              family
              familyLabel
              type
              typeLabel
              description
              date
              number
              amount
              currency
              isPaid
              remaining
              originalAmount
              taxRate
              issuerId
              issuerName
              receiverId
              receiverName
              matchs {
                inMatch {
                  id
                  amount
                  selected
                  currencyType
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                  }
                  accountPlan {
                    level1 {
                      value
                      label
                    }
                    level2 {
                      value
                      label
                    }
                    level3 {
                      value
                      label
                    }
                  }
                }
                outMatch {
                  id
                  amount
                  selected
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                  }
                  accountPlan {
                    level1 {
                      value
                      label
                    }
                    level2 {
                      value
                      label
                    }
                    level3 {
                      value
                      label
                    }
                  }
                }
              }
            }
          }
      }
    }
  }
`;

export const GetLinkedCuadraturaDocuments = ({ companyId, period }) => {
  const [result, reexecuteQuery] = useQuery({
    query: LINKED_CUADRATURA_DOCUMENTS,
    variables: { companyId, period },
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CREATE_CLASSPASS_OBLIGATIONS = `
mutation createObligationDocuments(
  $companyId: String!,
  $documents: [ObligationClassPassInput!]!,
) {
  createObligationDocuments(
    companyId: $companyId,
    documents: $documents,
  )
}`;

export const CreateClassPassObligations = () => {
  const [result, executeMutation] = useMutation(
    CREATE_CLASSPASS_OBLIGATIONS
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};


const CUADRATURA_PERIODS = `query
     CUADRATURA_PERIODS(
        $companyId: String!, 
    ) { 
        getCuadraturaPeriods(companyId: $companyId) 
}`


export const GetCuadraturaPeriods = ({ companyId }) => {
  const [result, reexecuteQuery] = useQuery({
    query: CUADRATURA_PERIODS,
    variables: { companyId },
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const LINK_CLASSPASS_OBLIGATIONS = `
mutation linkCuadraturaDocuments(
  $companyId: String!,
  $input: [LinkCuadraturaInput!]!,
) {
  linkCuadraturaDocuments(
    companyId: $companyId,
    input: $input,
  )
}`;

export const LinkClassPassObligations = () => {
  const [result, executeMutation] = useMutation(
    LINK_CLASSPASS_OBLIGATIONS
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const UN_LINK_CLASSPASS_OBLIGATIONS = `
mutation unLinkCuadraturaDocuments(
  $companyId: String!,
  $input: [LinkCuadraturaInput!]!,
) {
  unLinkCuadraturaDocuments(
    companyId: $companyId,
    input: $input,
  )
}`;

export const UnLinkClassPassObligations = () => {
  const [result, executeMutation] = useMutation(
    UN_LINK_CLASSPASS_OBLIGATIONS
  );
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CUADRATURA_LAST_PERIODS = `query
     CUADRATURA_LAST_PERIODS(
        $companyId: String!, 
    ) { 
        getCuadraturaLastPeriod(companyId: $companyId) 
}`


export const GetCuadraturaLastPeriod = ({ companyId }) => {
  const [result, reexecuteQuery] = useQuery({
    query: CUADRATURA_LAST_PERIODS,
    variables: { companyId },
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CUADRATURA_PROGRESS = `query
     CUADRATURA_PROGRESS(
        $companyId: String!, 
    ) { 
        getCuadraturaProgress(companyId: $companyId)  {
          _id
          sum
          linked
          notLinked
        }
}`


export const GetCuadraturaProgress = ({ companyId }) => {
  const [result, reexecuteQuery] = useQuery({
    query: CUADRATURA_PROGRESS,
    variables: { companyId },
  })

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_CASH_POSITION_STRUCTURE = `
  query GET_CASH_POSITION_STRUCTURE($companyId: String!, $name: String) {
    getCashPositionStructure(companyId: $companyId, name: $name) {
      name
      reportDate
      report {
        resume {
          week {
            BALANCE {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                }
              }
            }
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                }
              }
            }
            RESUME {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                }
              }
            }
          }
          biweek {
            BALANCE {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                }
              }
            }
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                }
              }
            }
            RESUME {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                }
              }
            }
          }
          month {
            BALANCE {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                }
              }
            }
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                }
              }
            }
            RESUME {
              label
              categoryKey
              values {
                fieldValue
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GetCashPositionStructure = ({ companyId, name }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_CASH_POSITION_STRUCTURE,
    variables: { companyId, name },
    pause: !companyId,
    requestPolicy: 'network-only', // Always fetch fresh data from database
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_CASH_POSITION = `
  query GET_CASH_POSITION($companyId: String!, $name: String) {
    getCashPosition(companyId: $companyId, name: $name) {
      name
      reportDate
      report {
        resume {
          week {
            BALANCE {
              label
              categoryKey
              values {
                fieldValue
                documents
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                  documents
                }
              }
            }
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue
                documents
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                  documents
                }
              }
            }
            RESUME {
              label
              categoryKey
              values {
                fieldValue
                documents
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                  documents
                }
              }
            }
          }
          biweek {
            BALANCE {
              label
              categoryKey
              values {
                fieldValue
                documents
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                  documents
                }
              }
            }
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue  
                documents
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                  documents
                }
              }
            }
            RESUME {
              label
              categoryKey
              values {
                fieldValue
                documents
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue  
                  documents
                }
              }
            }
          }
          month {
            BALANCE {
              label
              categoryKey
              values {
                fieldValue  
                documents
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue
                  documents
                }
              }
            }
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue  
                documents
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                  documents
                }
              }
            }
            RESUME {
              label
              categoryKey
              values {
                fieldValue  
                documents
              }
              fields {
                label
                fieldKey
                values {
                  fieldValue  
                  documents
                }
              }
            }
          }
        }
        detailed {
          day {
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue
                documents
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                  documents
                }
              }
            }
          }
          week {
            EXPENSE {
              label
              categoryKey
              values {
                fieldValue
                documents
              }
              fields {
                fieldKey
                label
                values {
                  fieldValue
                  documents
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GetCashPosition = ({ companyId, name, paused = true }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_CASH_POSITION,
    variables: { companyId, name },
    pause: paused || !companyId,
    requestPolicy: 'network-only', // Always fetch fresh data
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};






